import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import Flex from "common/Boxes/Flex";
import { round } from "@shared/utils/numbers";
import { swapArrayLoc } from "@shared/utils/arrays";
import { Skeleton } from "common/Skeleton/Skeleton";
import LineSkeleton from "common/Skeleton/LineSkeleton";
import styled, { useTheme } from "styled-components";
import Tooltip from "common/Tooltips/Tooltip";

interface HorizontalPieChartProps<T> {
  items: T[];
  id: string;
  getLegendExtraData?: (item: T) => { name: ReactNode; value: ReactNode }[];
  showSkeleton?: boolean;
  skeletonActive?: boolean;
}

const HorizontalPieChart = <T extends DataItem>({
  items,
  id,
  getLegendExtraData,
  showSkeleton,
  skeletonActive,
}: HorizontalPieChartProps<T>) => {
  const wrapperId = `legend_wrapper_${id}`;
  const theme = useTheme();
  const observerRef = useRef<IntersectionObserver>(undefined);
  const [itemsRefs, setItemsRefs] = useState<Record<string, HTMLDivElement>>(
    {}
  );
  const observedItemsRef = useRef(new Set<string>(undefined));
  const [visibleItems, setVisibleItems] = useState(new Set<string>());
  const [focusedItem, setFocusedItem] = useState<string | undefined>();
  const chartColors = useMemo(
    () => swapArrayLoc(theme.colors.pieChartColors, 0, 1),
    [theme]
  );
  const toggleItemVisibility = (name: string, visible: boolean) => {
    setVisibleItems((prev) => {
      if (visible && !prev.has(name)) {
        return new Set([...prev, name]);
      } else if (!visible && prev.has(name)) {
        return new Set([...prev].filter((value) => value !== name));
      }
      return prev;
    });
  };

  const getLegendItemId = (key: string) => `${LEGEND_ITEM_ID_PREFIX}${key}`;
  // Keyed by position as well as name: an account can hold two entries with
  // the same name (e.g. a long and a short on one pair).
  const getItemKey = (item: DataItem) =>
    `${items.indexOf(item as T)}-${item.name}`;

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (e) => {
        e.forEach((element) => {
          const shouldDisplay = element.intersectionRatio === 1;
          toggleItemVisibility(
            element.target.id.replace(LEGEND_ITEM_ID_PREFIX, ""),
            shouldDisplay
          );
        });
      },
      {
        root: document.querySelector(`#${wrapperId}`),
        // New look: the legend itself leaves room for "+N" (see
        // LegendWrapper), so an entry that only partly fits is hidden rather
        // than drawn under the count.
        rootMargin: `0px ${theme.isNewLook ? 0 : -PADDING_RIGHT}px 0px 0px`,
        threshold: 1.0,
      }
    );
    return () => {
      observedItemsRef.current.forEach((name) => {
        const element = itemsRefs[name];
        if (element) {
          observerRef.current?.unobserve(element);
        }
      });
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    Object.entries(itemsRefs).forEach(([name, element]) => {
      if (!observedItemsRef.current.has(name)) {
        observerRef.current?.observe(element);
        observedItemsRef.current.add(name);
      }
    });
  }, [itemsRefs]);

  const addLegendItem = (element: HTMLDivElement | null, name: string) => {
    setItemsRefs((prevItems) => {
      if (element && !(name in prevItems)) {
        return { ...prevItems, [name]: element };
      }
      return prevItems;
    });
  };

  const getColor = (index: number) =>
    chartColors[index % (chartColors.length - 1)];

  const getPrecisedValue = (value: number) => {
    const precision = value < 1 ? 2 : 1;
    return round(value, precision);
  };

  const getLegendItem = (item: DataItem, index: number, _color?: string) => {
    const color = _color ?? getColor(index);
    const itemKey = getItemKey(item);
    return (
      <LegendItemWrapper
        key={index + item.name}
        ref={(element: HTMLDivElement | null) => {
          addLegendItem(element, itemKey);
        }}
        $invisible={!visibleItems.has(itemKey)}
        id={getLegendItemId(itemKey)}
      >
        {showSkeleton ? (
          <SkeletonCircle active={skeletonActive} />
        ) : (
          <Circle $color={color} />
        )}
        {showSkeleton ? (
          <LineSkeleton
            height={15}
            style={{ width: 70 }}
            active={skeletonActive}
          />
        ) : (
          <>
            <LegendText>{item.name}</LegendText>
            <LegendText type="secondary" style={{ marginLeft: 4 }}>
              {getPrecisedValue(item.value)}%
            </LegendText>
          </>
        )}
      </LegendItemWrapper>
    );
  };

  const invisibleItems = useMemo(() => {
    return items.filter(
      (item, index) => !visibleItems.has(`${index}-${item.name}`)
    );
  }, [visibleItems, items]);

  const getInvisibleItemsLegend = () => {
    if (!invisibleItems.length || showSkeleton) return;
    return (
      <LegendItemWrapper
        $invisible={false}
        style={{ position: "absolute", right: 0, top: 2 }}
      >
        <LegendText>+{invisibleItems.length}</LegendText>
      </LegendItemWrapper>
    );
  };

  const getTooltipContent = (item: T) => {
    const extraItems = getLegendExtraData?.(item);

    return (
      <div>
        <Text style={{ color: "white" }} $size="S">
          {item.name} ({getPrecisedValue(item.value)}%)
        </Text>
        {!!extraItems && (
          <LegendExtraItemsWrapper>
            {extraItems.map((item, index) => {
              return (
                <Flex key={index}>
                  <Text style={{ color: "#B8BEC8", marginRight: 20 }} $size="S">
                    {item.name}
                  </Text>
                  <Text style={{ color: "white" }} $size="S" as="div">
                    {item.value}
                  </Text>
                </Flex>
              );
            })}
          </LegendExtraItemsWrapper>
        )}
      </div>
    );
  };

  const getItem = (item: T, index: number) => {
    const color = getColor(index);
    return (
      <Tooltip
        title={getTooltipContent(item)}
        onOpenChange={(isOpen) => {
          if (isOpen) setFocusedItem(item.name);
          else setFocusedItem(undefined);
        }}
        key={index}
      >
        <ItemWrapper $value={item.value}>
          <Line
            $color={color}
            $unfocused={!!focusedItem && focusedItem !== item.name}
          />
        </ItemWrapper>
      </Tooltip>
    );
  };

  const filteredItems = useMemo(() => {
    if (showSkeleton)
      return [
        { name: "loading1", value: 30 },
        { name: "loading2", value: 20 },
        { name: "loading3", value: 10 },
        { name: "loading4", value: 10 },
        { name: "loading5", value: 5 },
      ] as T[];
    return items.filter((item) => item.value > 0.1);
  }, [items, showSkeleton]);

  return (
    <div>
      <ItemsWrapper>
        {showSkeleton ? (
          <LineSkeleton
            style={{
              width: "100%",
              padding: "4px 0",
            }}
            active={skeletonActive}
            height={LINE_HEIGHT}
          />
        ) : (
          filteredItems.slice(0, 20).map(getItem)
        )}
      </ItemsWrapper>
      <div style={{ position: "relative" }}>
        <LegendWrapper id={wrapperId}>
          {filteredItems
            .slice(0, 10)
            .map((item, index) => getLegendItem(item, index))}
        </LegendWrapper>
        {getInvisibleItemsLegend()}
      </div>
    </div>
  );
};

export default HorizontalPieChart;

type DataItem = { name: string; value: number };

const LEGEND_ITEM_ID_PREFIX = "legend_item_";
const PADDING_RIGHT = 52;
/** Room for the "+N" count after the new look's legend. */
const NEW_LOOK_COUNT_WIDTH = 32;
const LINE_HEIGHT = 6;
const LEGEND_CIRCLE_SIZE = 6;

const ItemsWrapper = styled.div`
  display: flex;
  column-gap: 2px;
`;
const ItemWrapper = styled.div<{ $value: number }>`
  padding: 4px 0;
  flex: ${(p) => Math.max(p.$value, 1.2)} 1 auto;
`;
const Line = styled.div<{
  $color: string;
  $unfocused?: boolean;
}>`
  height: ${LINE_HEIGHT}px;
  border-radius: 2px;
  background-color: ${(p) => p.$color};
  transition: opacity 0.25s;
  ${(p) =>
    p.$unfocused &&
    `
    opacity: 0.3;
  `};
`;
const LegendText = styled(Text).attrs({
  $weight: typography.fontWeights.MEDIUM,
  $size: "S",
})`
  @media (min-width: 601px) {
    ${(p) =>
      p.theme.isNewLook &&
      p.type === "secondary" &&
      `color: ${p.theme.colors.mediumText};`}
  }
`;
const LegendWrapper = styled.div`
  display: flex;
  column-gap: 12px;
  margin-top: 6px;
  overflow: hidden;
  ${(p) => p.theme.isNewLook && `margin-right: ${NEW_LOOK_COUNT_WIDTH}px;`}
`;
const LegendExtraItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 6px;
  margin-top: 8px;
`;
const SkeletonCircle = styled(Skeleton.Avatar).attrs({
  shape: "circle",
  size: LEGEND_CIRCLE_SIZE,
})`
  margin-right: 6px;
  display: flex !important;
  align-items: center;
`;
const Circle = styled.div<{ $color: string }>`
  height: ${LEGEND_CIRCLE_SIZE}px;
  width: ${LEGEND_CIRCLE_SIZE}px;
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  margin-right: 6px;
`;
const LegendItemWrapper = styled.div<{ $invisible?: boolean }>`
  display: flex;
  align-items: center;
  visibility: ${(p) => (p.$invisible ? "hidden" : "initial")};
`;
